/**
 * Vocabulary key canonicalization (CH-6).
 *
 * `knownCharacters` keys are SIMPLIFIED, always. Without that, the triage
 * card would write whatever script was DISPLAYED (Traditional mode wrote
 * 紅, Simplified wrote 红), and the overlay filter would compare against the
 * displayed char — so vocabulary would silently stop matching whenever the
 * script toggle changed, and the same character could exist twice.
 *
 * Three layers, all through {@link canonicalizeChar}:
 *  1. write path  — useVocabularyStore actions canonicalize before writing;
 *  2. read path   — PinyinOverlay's suppression filter and the triage
 *     card's isKnown canonicalize the DISPLAYED char before lookup;
 *  3. the CRDT v7 migration (src/app/migrations.ts) rewrites existing docs
 *     once: traditional keys merge into their simplified form with
 *     min-timestamp semantics, deterministically on every client.
 *
 * The table is COMMITTED (src/domains/chinese/vocabulary/trad2simp.json,
 * generated by scripts/generate-trad2simp.mjs from opencc-js): it inverts
 * the exact cn→tw mapping the reader displays with, and being
 * code-versioned keeps the v7 transform deterministic across clients —
 * vocabulary keys are single characters by construction of the triage UI,
 * so the full async OpenCC converter is unnecessary at the store boundary.
 */
import trad2simp from './trad2simp.json';

const TABLE = trad2simp as Record<string, string>;

/**
 * Canonical (simplified) form of a single character. Identity for anything
 * already simplified, non-Han, or unknown to the table.
 */
export function canonicalizeChar(char: string): string {
  // Own-property check: a bare `TABLE[char]` would resolve inherited keys
  // ('constructor', '__proto__', ...) to Object.prototype members.
  return Object.prototype.hasOwnProperty.call(TABLE, char) ? TABLE[char] : char;
}

/**
 * Deterministic merge of two timestamps for the same canonical key
 * (the v7 LWW-safe rule: earliest knowledge wins — `min` of the numeric
 * values; a lone numeric value wins over a malformed one).
 */
export function mergeCanonicalTimestamps(
  existing: unknown,
  incoming: unknown,
): number | undefined {
  const existingTs = typeof existing === 'number' && Number.isFinite(existing) ? existing : undefined;
  const incomingTs = typeof incoming === 'number' && Number.isFinite(incoming) ? incoming : undefined;
  if (existingTs !== undefined && incomingTs !== undefined) return Math.min(existingTs, incomingTs);
  return existingTs ?? incomingTs;
}

/** The raw table (read-only) — exposed for the invariant test suite. */
export function getTrad2SimpTable(): Readonly<Record<string, string>> {
  return TABLE;
}
